import { AfterViewInit, Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormlyInputType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { FormattingService } from '@app/shared/services/formatting.service';
import { CompanyDataService } from '@app/shared/company/company-data.service';
import { WebshopDataService } from '../../webshop/webshop-data.service';
import { ICompany, IObject, IValueObject } from '@type_definitions/datatypes';
import { AuthService } from '@app/shared/services/auth/auth.service';
import * as _ from 'underscore';
import { HttpClient } from '@angular/common/http';
import { GaValidateService, IValidatingComponent, IValidatingComponentConfig, IValidationResults } from '@app/shared/formly/ga-validate.service';
import { SimpleTestCustomComponent } from './simple-test-custom.component';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { EDITOR_TINYMCE, TINYMCE_SIMPLE } from '@app/shared/editor/editor-config.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { testEmailAddressFn } from '@app/shared/formly/ga-formly.util';

@Component({
  selector: 'ngx-formly-demo',
  template: `
	  <formly [form]="form" [fields]="fields" [model]="model" class="test-class" [noValidate]="true">
			<button class="btn btn-primary" (click)="validateAll()" style="margin-right: 6px">Validate Component</button>
			<button class="btn btn-primary" (click)="validateFields()">Validate Fields</button>
		</formly>
    <div style="display:none">
      <ng-template #template></ng-template>
    </div>
	`,
  providers: [
    CompanyDataService,
    WebshopDataService
  ]
})
export class NgxFormlyDemoComponent implements OnInit, AfterViewInit, IValidatingComponent {
  @ViewChild('template', { read: ViewContainerRef }) public template:ViewContainerRef;
  private onValidationReadyDeferred:IGADeferred<void>; // necessary for validateComponent()

  public model:any = {
    testValue: {
      id: 1,
      name: 'Triadi Prabowo'
    },
    testMultiSelectB: [
      {
        id: 699,
        name: 'ABB'
      }
    ],
    testSelect2: {
      id: 1473,
      name: 'ACI WORLDWIDE LENOVO'
    },
    testSelect2A: {
      id: 2,
      name: 'Koen Weyn'
    },
    testSelect2B: {
      id: 2,
      name: 'Koen Weyn'
    },
    testSelectC: {
      id: 1,
      name: 'Triadi Prabowo'
    },
    testSelectD: {
      id: 99,
      name: 'Triadi Prabowo'
    },
    email: 'bla',
    url: 'bla',
    num: 'bla',
    maxNum: 1000,
    maxLength: '01234567890',
    testDateInput: 1606301741034,
    testRadio: { id: true },
    formDirty: false,
    selectDirty: false,
    select2Dirty: false
  };
  public fields:IExtraFieldConfigurationObject[];
  public form:UntypedFormGroup = new UntypedFormGroup({});

  private selectA:IExtraFieldConfigurationObject;
  private selectB:IExtraFieldConfigurationObject;
  private select2A:IExtraFieldConfigurationObject;

  private multiSelectA:IExtraFieldConfigurationObject;
  private multiSelectB:IExtraFieldConfigurationObject;

  private searchB:IExtraFieldConfigurationObject;
  private dirtyStateField:IExtraFieldConfigurationObject;
  private selectDirtyStateField:IExtraFieldConfigurationObject;
  private select2DirtyStateField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private companyDataService:CompanyDataService,
    private webshopDataService:WebshopDataService,
    private authService:AuthService,
    private http:HttpClient,
    private validateService:GaValidateService,
    private messageService:GaMessageService,
    private translationService:TranslationService,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.model.testMultiSelectA = [
      {
        id: this.authService.getCurrentUser().id,
        firstname: this.authService.getCurrentUser().firstname,
        lastname: this.authService.getCurrentUser().lastname
      },
      'manual_email@acslworld.com'
    ];

    this.fields = this.createFields();

    // fill fields
    this.fillFields();

    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private listen():void {
    this.form.valueChanges.subscribe((model:IObject) => {
      console.log(model);

      if (this.dirtyStateField && !_.isEqual(model.formDirty, this.form.dirty)) {
        this.dirtyStateField.formControl.setValue(this.form.dirty);
      }

      if (this.selectDirtyStateField && !_.isEqual(model.selectDirty, this.selectA.formControl.dirty)) {
        this.selectDirtyStateField.formControl.setValue(this.selectA.formControl.dirty);
      }

      if (this.select2DirtyStateField  && !_.isEqual(model.select2Dirty, this.select2A.formControl.dirty)) {
        this.select2DirtyStateField.formControl.setValue(this.select2A.formControl.dirty);
      }
    });
  }

  private createFields = ():IExtraFieldConfigurationObject[] => {
    const labelClass = 'col-sm-3';
    const valueClass = 'col-sm-9';

    return [
      this.formlyService.aTitle('Form State')
        .build(),
      this.dirtyStateField = this.formlyService.aValue('formDirty', 'Form Dirty')
        .build(),
      this.formlyService.aTitle('Select & Select2')
        .build(),
      this.selectA = this.formlyService.aSelect('testSelect', 'WEBSHOP.WEBSHOP_INFO.CUSTOMERCOMPANY.LABEL')
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.onSelectAChange)
        .build(),
      this.selectDirtyStateField = this.formlyService.aValue('selectDirty', 'Company name dirty state')
        .build(),
      this.selectB = this.formlyService.aSelect('testSelectB', 'Test select (with group)')
        .isRequired()
        .withLabelProp('description')
        .withValueProp('value')
        .withGroupProp('group')
        .onChange(this.onSelectBChange)
        .build(),
      this.formlyService.aSelect('testSelectC', 'Test select initial value (without options)')
        .withFormat(this.formattingService.formatName)
        .build(),
      this.formlyService.aSelect('testSelectD', 'Test select initial value (not in options)')
        .withFormat(this.formattingService.formatName)
        .withOptions([
          {
            id: 2,
            name: 'Koen Weyn'
          },
          {
            id: 3,
            name: 'Marius Hirjanu'
          }
        ])
        .build(),
      this.select2A = this.formlyService.aSelect2('testSelect2', 'Test select2')
        .isRequired()
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.select2DirtyStateField = this.formlyService.aValue('select2Dirty', 'Test select2 dirty state')
        .build(),
      this.formlyService.aSelect2('testSelect2A', 'Test select2 initial value (without options)')
        .withFormat(this.formattingService.formatName)
        .build(),
      this.formlyService.aSelect2('testSelect2B', 'Test select2 initial value (not in options)')
        .withFormat(this.formattingService.formatName)
        .withOptions([
          {
            id: 1,
            name: 'Triadi Prabowo'
          },
          {
            id: 3,
            name: 'Marius Hirjanu'
          }
        ])
        .build(),
      this.multiSelectA = this.formlyService.aMultiSelect('testMultiSelectA', 'WEBSHOP.MAIN_CONTACT')
        .isRequired()
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.companyDataService.getVGAMContacts(filter, 960)
            },
            minSearchLength: 1,
            customValueTestFn: testEmailAddressFn
          }
        })
        .withValueClass(valueClass)
        .withLabelClass(labelClass)
        .build(),
      this.multiSelectB = this.formlyService.aMultiSelect('testMultiSelectB', 'Test multiselect (localSearch)')
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),
      this.formlyService.aMultiSelect('testMultiSelectC', 'Multiselect (valueProp & labelProp)')
        .withLabelProp('firstname')
        .withValueProp('id')
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withOptions([
          { id: 1, firstname: 'Triadi 1', lastname: 'Prabowo' },
          { id: 2, firstname: 'Triadi 2', lastname: 'Prabowo' }
        ])
        .build(),
      this.formlyService.aLink('Ng-select demo')
        .withExtraOptions({
          templateOptions: {
            href: '/ga2/#/demo/ng-select',
            target: '_self'
          }
        })
        .build(),
      // ---------------------
      this.formlyService.aTitle('Group / No wrapper')
        .build(),
      this.formlyService.aGroup('testGroup', 'Group')
        .withFields([
          this.formlyService.anInput('street', 'Street')
            .withoutWrapper()
            .isRequired()
            .withWrapperClass('col-space-bottom col-sm-12')
            .build(),
          this.formlyService.anInput('zip', 'GA.GENERAL.ZIP')
            .withoutWrapper()
            .withWrapperClass('col-4 col-xsm-5 col-sm-4 col-hug-right')
            .build(),
          this.formlyService.anInput('city', 'GA.GENERAL.CITY')
            .withoutWrapper()
            .withWrapperClass('col-8 col-xsm-7 col-sm-8 col-hug-left')
            .build()
        ])
        .build(),
      this.formlyService.aSelect('testSelectNoWrapper')
        .withoutWrapper()
        .build(),
      // ---------------------
      this.formlyService.aTitle('Input, value, and search')
        .build(),
      this.formlyService.anInput('testInput', 'Test input')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo(this.translationService?.instant('GA.FORM.REQUIRED', { label: 'Test input' }))
        .onBlur((value:any) => console.log('TEST INPUT', value))
        .withPlaceholder()
        .build(),
      this.formlyService.anInput('testNumber', 'Test input number (with Max)')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInputType(FormlyInputType.NUMBER)
        .withAddOn('GA.GENERAL.DAYS')
        .withExtraOptions({
          templateOptions: {
            max: 10
          }
        })
        .build(),
      this.formlyService.anInput('testInputLoading', 'Test input (with Loading)')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withLoading(true)
        .build(),
      this.formlyService.aValue('testValue', 'Test value')
        .withFormat(this.formattingService.formatName)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withClipboard()
        .build(),
      this.formlyService.aSearch('testSearch', 'Test search (from XHR)')
        .withFormat(this.formattingService.formatContact)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.companyDataService.getVGAMContacts(filter, 960)
            },
            minSearchLength: 1
          }
        })
        .build(),
      this.searchB = this.formlyService.aSearch('testSearch2', 'Test search (from localSearch)')
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            localSearch: true,
            minSearchLength: 2
          }
        })
        .build(),
      this.formlyService.aSearch('testSearch3', 'Test search (from remote search)')
        .withFormat(this.formattingService.formatName)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            minSearchLength: 1,
            params: {
              oemId: 960
            }
          }
        })
        .withUrl('/ga2/rest/globalproduct/models')
        .build(),
      this.formlyService.aHidden('testHidden')
        .build(),
      // ----------------------------
      this.formlyService.aTitle('Validation')
        .build(),
      this.formlyService.anInput('email', 'E-mail')
        .withInputType(FormlyInputType.EMAIL)
        .build(),
      this.formlyService.anInput('maxLength', 'Max length')
        .withMaxLength(10)
        .build(),
      this.formlyService.anInput('url', 'URL')
        .withInputType(FormlyInputType.URL)
        .build(),
      this.formlyService.anInput('num', 'Number')
        .withInputType(FormlyInputType.NUMBER)
        .build(),
      this.formlyService.anInput('maxNum', 'Maximum Number')
        .withInputType(FormlyInputType.NUMBER)
        .withExtraOptions({
          templateOptions: {
            max: 10
          }
        })
        .build(),
      this.formlyService.aTitle('Checkbox & Radio')
        .build(),
      this.formlyService.aCheckbox('testCheckbox', 'Test checkbox')
        .withoutWrapper()
        .build(),
      this.formlyService.aRadio('testRadio', 'Test radio')
        .isRequired()
        .withFormat(this.formattingService.formatLov)
        .withWrapperClass('radio-horizontal')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
        .build(),
      this.formlyService.aRadioButton('testRadioButton', 'Test radio button')
        .withOption('RADIO')
        .build(),
      // -------------------------
      this.formlyService.aTitle('Textarea & TinyMCE')
        .build(),
      this.formlyService.aTextarea('testTextarea', 'Test textarea')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aTextarea('tinymce', 'TinyMCE')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            editor: EDITOR_TINYMCE,
            editorCode: TINYMCE_SIMPLE
          }
        })
        .build(),
      // --------------------------
      this.formlyService.aTitle('Group')
        .build(),
      this.formlyService.aGroup(undefined, 'Group 1')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFields([
          this.formlyService.aCheckbox('testCheckbox2', 'I agree with this group checkbox!')
            .withoutWrapper()
            .withWrapperClass('col-sm-12')
            .build()
        ])
        .build(),
      // --------------------------
      this.formlyService.aTitle('Template & Custom')
        .build(),
      this.formlyService.aTemplate('<b>Koen</b> <em>Weyn</em>')
        .build(),
      this.formlyService.aCustom('Custom')
        .withComponent(SimpleTestCustomComponent)
        .build(),
      // --------------------------
      this.formlyService.aTitle('Date Input')
        .build(),
      this.formlyService.aDateInput('testDateInput', 'Test date input')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            inputFocus: true
          }
        })
        .build(),
      // --------------------------
      this.formlyService.aTitle('Utilities (Disabled, Inline, etc.)')
        .build(),
      this.formlyService.anInput('testDisabledInput', 'Test disabled input (withDisabled)')
        .withDisabled(true)
        .build(),
      this.formlyService.aSearch('testDisabledSearch', 'Test disabled search')
        .isDisabled()
        .build(),
      this.formlyService.aGroup(undefined, 'Test disabled checkbox')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFields([
          this.formlyService.aCheckbox('testDisabledCheckbox')
            .withoutWrapper()
            .withWrapperClass('col-sm-12')
            .isDisabled()
            .build()
        ])
        .build()
    ];
  };

  private fillFields():void {
    this.formlyService.fillField(this.selectA, this.companyDataService.customerCompanies());
    this.formlyService.fillField([
      this.select2A,
      this.searchB,
      this.multiSelectB
    ], this.webshopDataService.webshops());

    this.formlyService.fillField(this.selectB, this.loadProperties())
      .then(properties => {
        const options:any[] = [];

        _.forEach(properties.data, (propertyList:any) => {
          const propListModel:any = _.findWhere(this.model[ 'testSelectB' ], { name: propertyList.name });
          _.forEach(propertyList.values, (property:any) => {
            let propModel:any;
            if (propListModel?.values) {
              propModel = _.findWhere(propListModel.values, { name: property.name });
            }
            if (!propModel) {
              const option:any = {
                description: property.name,
                value: {
                  list: propertyList.name,
                  field: property.name,
                  control: property.control,
                  definitionId: property.definitionId
                },
                group: propertyList.name
              };
              options.push(option);
            }
          });
        });
        this.selectB.formControl?.patchValue(options?.[ 0 ]?.value);
        this.selectB.templateOptions.options = options;
      });
  }

  private loadProperties():Promise<any> {
    return this.http.get(this.authService.authenticatedUrl('/ga2/rest/globalproduct/properties'))
      .toPromise();
  }

  private onSelectAChange = (newCompanyValue:ICompany, oldCompanyValue:ICompany):void => {
    console.log('NEW VALUE - SELECT A:', newCompanyValue);
    console.log('OLD VALUE - SELECT A:', oldCompanyValue);
  };

  private onSelectBChange = (newValue, oldValue) => {
    console.log(newValue, oldValue);
  };

  public beforeValidate = ():Promise<void> => {
    console.log('beforeValidate() executed');
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<void|string[]> => {
    console.log('validate() executed');
    return this.validateService.validateFields(this.fields, this.model);
  };

  public validateAll():void {
    const componentsToValidate:IValidatingComponentConfig[] = [{
      id: 'NGX Formly Demo',
      class: NgxFormlyDemoComponent
    }];
    this.validateService.validateComponents(componentsToValidate, this.template)
      .then(() => {
        this.messageService.showMessageDialog('Validation successful', 'Validation succeeded');
      })
      .catch((results:IValidationResults) => {
        this.messageService.showInvalidFormMessage('Validation failed', undefined, results.allErrors);
      });
  }

  public validateFields():Promise<void|string[]> {
    console.log('validateFields() executed');
    return this.validateService.validateFields(this.fields, this.model, true)
      .catch((errors:string[]) => {
        console.log('validateFields() errors', errors);
      });
  }
}